import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { LucideIcon } from 'lucide-react';
import { useMarketingAuthStore } from '../../../store/marketingAuthStore';
import { useEntitlements } from './useEntitlements';
import { useWorkspaceProfile } from './useWorkspaceProfile';
import { NAV_HUBS, visibleNav, visibleUnlisted } from '../navigation';

/** A single navigable destination the command palette can jump to. */
export interface NavCommand {
  /** Stable id / React key (the path). */
  id: string;
  /** Localized page label. */
  label: string;
  /** Localized owning-hub label (null for single-page hubs like Dashboard). */
  hubLabel: string | null;
  /** Router path. */
  path: string;
  icon?: LucideIcon;
}

/**
 * Flattens the SAME role/plan/agency-gated navigation the sidebar renders
 * (`visibleNav(NAV_HUBS, …)`) into a flat list of destinations for the command
 * palette. Because it reuses `visibleNav`, the palette can never jump a user to
 * a page their role/plan can't see. Deduped by path (each page lives in exactly
 * one hub, but the Set guards against future overlap).
 */
export function useNavCommands(): NavCommand[] {
  const { t } = useTranslation('marketing');
  const user = useMarketingAuthStore((s) => s.user);
  const { has } = useEntitlements();
  const { isAgency } = useWorkspaceProfile();
  const isManager = user?.role === 'MANAGER' || user?.role === 'OWNER';
  // The Agency hub is `ownerOnly` in navigation.ts, so the palette must pass
  // isOwner too — otherwise an agency OWNER can't jump to /agency/* pages the
  // sidebar (which does pass isOwner) shows them.
  const isOwner = user?.role === 'OWNER';

  return useMemo(() => {
    const hubs = visibleNav(NAV_HUBS, { isManager, isOwner, has, isAgency });
    const commands: NavCommand[] = [];
    const seen = new Set<string>();
    const push = (c: NavCommand) => {
      if (seen.has(c.path)) return;
      seen.add(c.path);
      commands.push(c);
    };
    for (const hub of hubs) {
      const hubLabel = t(hub.labelKey, hub.label);
      if (hub.children && hub.children.length > 0) {
        for (const child of hub.children) {
          push({
            id: child.path,
            label: t(child.labelKey, child.label),
            hubLabel,
            path: child.path,
            icon: child.icon ?? hub.icon,
          });
          /**
           * A merged page's tabs are destinations too.
           *
           * Six pairs of settings pages became six pages with tabs, which made
           * the sidebar shorter and would — without this — have made the
           * palette worse: somebody who knows the thing is called "Roles" types
           * it, finds nothing, and concludes it was removed. The list is
           * something you SCAN and the palette is something you ASK, and only
           * the list was too long.
           *
           * The first tab is skipped: it is what the bare path already opens,
           * and offering it twice under two names is the duplication this
           * whole change is removing.
           */
          for (const sub of child.tabs?.slice(1) ?? []) {
            // A tab can be gated more tightly than the page that holds it —
            // /calls needed `telephony` before it became a tab of Voice. The
            // palette must not offer a jump to a half the workspace cannot use.
            if (!has(sub.feature)) continue;
            push({
              id: `${child.path}?tab=${sub.tab}`,
              label: t(sub.labelKey, sub.label),
              hubLabel,
              path: `${child.path}?tab=${sub.tab}`,
              icon: child.icon ?? hub.icon,
            });
          }
        }
      } else if (hub.path) {
        push({ id: hub.path, label: hubLabel, hubLabel: null, path: hub.path, icon: hub.icon });
      }
    }
    // Pages that are routable but hold no sidebar slot. Appended last so a
    // real hub always wins the dedupe if one ever adopts the same path.
    //
    // GATED, since 2026-09-01. This loop used to read the raw list, which was
    // safe only while that list held two pages every member may open. Stage 4
    // moved six more into it, one of them (`/appointments`) manager-only and
    // `funnels`-gated — and this hook is the whole door those six have left, so
    // an ungated read here would have handed a rep a jump into a page whose
    // every backend route answers 403.
    for (const d of visibleUnlisted({ isManager, isOwner, has, isAgency })) {
      push({ id: d.path, label: t(d.labelKey, d.label), hubLabel: null, path: d.path, icon: d.icon });
    }
    return commands;
    // `has` and `isAgency` come from cached queries; include primitives that
    // actually change the output. `t` is stable per language.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isManager, isOwner, isAgency, t, has]);
}
